import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';

interface SitemapSection {
  title: string;
  links: { label: string; path: string; desc?: string }[];
}

@Component({
  selector: 'app-sitemap',
  standalone: true,
  imports: [RouterLink],
  template: `
    <!-- Hero -->
    <section class="relative py-20 md:py-24 -mt-20 pt-28 md:pt-36 bg-surface-950 overflow-hidden">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-surface-950 to-brand-900"></div>
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <h1 class="font-heading text-3xl md:text-4xl font-extrabold text-white tracking-tight">Sitemap</h1>
        <p class="text-surface-400 mt-3 max-w-lg">A complete overview of all pages on our website.</p>
      </div>
    </section>

    <!-- Sitemap Grid -->
    <section class="py-16 md:py-24 bg-white">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          @for (section of sections; track section.title) {
            <div>
              <h2 class="font-heading text-sm font-bold uppercase tracking-wider text-surface-400 mb-5 pb-3 border-b border-surface-100">
                {{ section.title }}
              </h2>
              <ul class="space-y-1">
                @for (link of section.links; track link.path) {
                  <li>
                    <a [routerLink]="link.path"
                       class="group flex items-start gap-3 py-2.5 px-3 -mx-3 rounded-lg transition-colors hover:bg-surface-50">
                      <svg aria-hidden="true" class="w-4 h-4 text-surface-300 group-hover:text-brand-500 shrink-0 mt-0.5 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>
                      <div>
                        <span class="text-sm font-medium text-surface-800 group-hover:text-brand-600 transition-colors">{{ link.label }}</span>
                        @if (link.desc) {
                          <span class="block text-xs text-surface-400 mt-0.5 leading-relaxed">{{ link.desc }}</span>
                        }
                      </div>
                    </a>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      </div>
    </section>
  `,
})
export class SitemapComponent implements OnInit {
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  sections: SitemapSection[] = [];

  ngOnInit(): void {
    this.seo.setTitle('Sitemap');

    const pages = this.config.pages;

    // Main pages
    this.sections.push({
      title: 'Main Pages',
      links: [
        { label: 'Home', path: '/', desc: 'Welcome to ' + this.config.brand.name },
        { label: 'About Us', path: '/about', desc: 'Our story, values, and team' },
        { label: 'Services', path: '/services', desc: 'What we offer' },
        { label: 'Projects', path: '/projects', desc: 'Our portfolio of completed work' },
        { label: 'Contact', path: '/contact', desc: 'Get in touch with us' },
      ],
    });

    // Divisions (corporate only)
    if (this.config.isCorporate && pages.divisions) {
      const divLinks = pages.divisions.divisions.map(d => ({
        label: d.name,
        path: d.url || '/divisions',
        desc: d.desc.slice(0, 80) + (d.desc.length > 80 ? '...' : ''),
      }));
      this.sections.push({
        title: 'Our Divisions',
        links: [
          { label: 'All Divisions', path: '/divisions', desc: 'Overview of our specialized divisions' },
          ...divLinks,
        ],
      });
    }

    // Expertise (division only)
    if (!this.config.isCorporate && pages.expertise) {
      this.sections.push({
        title: 'Expertise',
        links: [
          { label: 'All Expertise', path: '/expertise', desc: 'Browse our areas of expertise' },
          ...pages.expertise.items.map(i => ({
            label: i.title,
            path: '/expertise/' + i.slug,
            desc: i.category,
          })),
        ],
      });
    }

    // Services
    if (pages.services.services.length) {
      this.sections.push({
        title: 'Services',
        links: pages.services.services.map(s => ({
          label: s.title,
          path: '/services',
          desc: s.desc.slice(0, 80) + (s.desc.length > 80 ? '...' : ''),
        })),
      });
    }

    // Projects
    if (pages.projects.projects.length) {
      this.sections.push({
        title: 'Projects',
        links: pages.projects.projects.map(p => ({
          label: p.title,
          path: '/projects/' + p.slug,
          desc: p.location,
        })),
      });
    }

    // Resources / Legal
    this.sections.push({
      title: 'Resources',
      links: [
        { label: 'Blog', path: '/blog', desc: 'News, tips, and industry updates' },
        { label: 'Sitemap', path: '/sitemap', desc: 'You are here' },
      ],
    });
  }
}
